/* eslint-disable @angular-eslint/directive-selector */
import { computed, Directive, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SelectComponent } from '../components/select.component';

/** Default minimum number of options above which the search input is shown in the panel. */
export const SELECT_SEARCH_MIN_ITEMS = 7;

/**
 * Adds text filtering to a `sim-select` fed by a data collection.
 *
 * The directive owns the search state: it automatically shows the panel search
 * input when the full collection exceeds `searchThreshold` (default
 * `SELECT_SEARCH_MIN_ITEMS`), filters the collection by the typed query and
 * clears the query when the panel closes. The host template renders
 * `filtered()` instead of the raw collection, so the select keeps working on
 * real options (panel height, keyboard navigation, typeahead).
 *
 * @example
 * <sim-select [simSelectSearch]="users()" [searchWith]="userSearchText" #search="simSelectSearch">
 *   @for (user of search.filtered(); track user.id) {
 *     <sim-option [value]="user.id">{{ user.name }}</sim-option>
 *   }
 * </sim-select>
 */
@Directive({
  selector: 'sim-select[simSelectSearch]',
  exportAs: 'simSelectSearch'
})
export class SelectSearchDirective<T> {
  private readonly _select = inject(SelectComponent, { self: true });
  private readonly _query = signal('');

  readonly items = input.required<readonly T[]>({ alias: 'simSelectSearch' });
  readonly searchWith = input<(item: T) => string>((item) => `${item}`);
  readonly searchThreshold = input<number>(SELECT_SEARCH_MIN_ITEMS);

  readonly filtered = computed<T[]>(() => {
    const query = this._query().trim().toLowerCase();
    const items = this.items();
    if (!query) return [...items];
    const searchWith = this.searchWith();
    return items.filter((item) => searchWith(item).toLowerCase().includes(query));
  });

  constructor() {
    this._select.inputValueChanges.pipe(takeUntilDestroyed()).subscribe((value) => this._query.set(value));
    this._select._closedStream.pipe(takeUntilDestroyed()).subscribe(() => this._query.set(''));
    effect(() => {
      this._select._setSearchState(this.items().length > this.searchThreshold(), this._query());
    });
  }
}
